import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getAxiosApiV2WithAuthorization, useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../../../config';
import { addLongPullUIItem, addNotification } from '../../../../store';
import { useDocumentZipDownload } from '../../../../hooks';

import { NgCard, NgCardContent, NgCardTitle } from '../../../UI';
import { OutputSelector } from './OutputSelector';
import { GenerateDocumentsSelector } from './GenerateDocumentsSelector';
import { DocumentsGenerateButton } from './DocumentsGenerateButton';
import { DocumentsPreviewDialog, DocumentsPreviewTemplateItem } from './DocumentsPreviewDialog';
import { useWorkspace } from 'apps/ng/src/app/context/WorkspaceProvider';
import { useFormContext } from 'react-hook-form';
import { CircularLoader } from 'apps/ng/src/app/shared';

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  h3 {
    margin-bottom: 0;
  }
`;

const StyledPreviewButton = styled.button`
  height: 26px;
  padding: 0 10px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  font-family: inherit;
  color: inherit;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.cardBorderDarker};
  border-radius: 99px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.palette.primaryButton};
    color: ${({ theme }) => theme.palette.primaryButton};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

interface GenerateDocumentsSectionProps {
  projectData: any;
  entityPrimaryKey?: string;
  isQueryValid: boolean;
  isQueriesEnabled: boolean;
  workspaceTemplateData?: { ID: number; DocumentTemplates: any[] };
  queriesResultsMap?: WorkspaceQueryInputResults | null;
  attributes?: { [key: string]: string }[];
  isFormDirty?: boolean;
  onBeforeGenerate?: () => Promise<void>;
}

export const GenerateDocumentsSection: FC<GenerateDocumentsSectionProps> = ({
  attributes,
  projectData,
  entityPrimaryKey,
  isQueryValid,
  isQueriesEnabled,
  queriesResultsMap,
  workspaceTemplateData,
  onBeforeGenerate,
  isFormDirty,
}) => {
  const dispatch = useDispatch();
  const { setLongPullItemId, isGenerating, isDocumentsGenerated, longPullItemId, generatingMessage } = useWorkspace();
  const { downloadZip } = useDocumentZipDownload();

  const {
    setValue,
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = useFormContext() || {};

  const [totalCount, setTotalCount] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [generatedForOutputType, setGeneratedForOutputType] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const selectedTemplates = watch('selectedDocumentTemplates');

  const [selectedOutput, setSelectedOutput] = useState<{
    type: string;
    path: string;
    folderId?: number | null;
  } | null>({ type: 'zip', path: '/' });

  const projectTemplatesToPickList = useDeepCompareMemo(() => {
    // @todo this should be from the designed getWorkspaceTemplate endpoint
    return (workspaceTemplateData?.DocumentTemplates || [])
      .map((item: any) => {
        if ((item?.AllowedOutputTypes || []).includes('html')) {
          return null;
        }

        return {
          id: item?.ID,
          name: item?.DisplayName || item?.OutputType || '',
          type: item?.OutputType,
          lastUpdated: item?.UpdatedAt,
          allowedOutputTypes: item?.AllowedOutputTypes || [],
          queryID: item?.Query?.ID,
          assetId: item?.AssetID,
          templateUrl: item?.TemplateUrl,
        };
      })
      .filter(Boolean);
  }, [workspaceTemplateData]);

  const previewTemplates: DocumentsPreviewTemplateItem[] = useDeepCompareMemo(() => {
    return (selectedTemplates || []).flatMap((selectedTemplate: GenerateDocumentTemplateItem) => {
      const template = projectTemplatesToPickList.find((item: any) => item?.id === selectedTemplate.id);

      if (!template) {
        return [];
      }

      return (selectedTemplate.output_types || []).map((outputType) => ({
        id: `${selectedTemplate.id}-${outputType}`,
        name: template.name,
        outputType,
        assetId: template.assetId,
      }));
    });
  }, [selectedTemplates, projectTemplatesToPickList]);

  const generateButtonState: { disabled: boolean; message: string | null } = useDeepCompareMemo(() => {
    if (isFormDirty) {
      return {
        disabled: true,
        message: 'You have unsaved changes. Please submit the form before generating.',
      };
    }

    if (!isQueryValid) {
      return {
        disabled: true,
        message: isQueriesEnabled
          ? 'Please run a valid query before generating documents.'
          : 'Please fill in all required fields before generating documents.',
      };
    }

    if (selectedTemplates?.length <= 0) {
      return {
        disabled: true,
        message: 'Please select at least one output type to proceed.',
      };
    }

    if (totalCount > config.etl.maxDocumentBuildLimit) {
      return {
        disabled: true,
        message: `Document generation limit exceeded. Maximum allowed: ${config.etl.maxDocumentBuildLimit}`,
      };
    }

    if (isGenerating) {
      return {
        disabled: true,
        message: null,
      };
    }

    if (!isValid) {
      return {
        disabled: true,
        message: null,
      };
    }

    return {
      disabled: false,
      message: null,
    };
  }, [selectedTemplates, isGenerating, isQueryValid, isFormDirty, totalCount, isValid]);

  const isDownloadReady =
    isDocumentsGenerated && selectedOutput?.type === 'zip' && generatedForOutputType === 'zip' && !isFormDirty;

  const handleOutputSelectionChange = (output: { type: string; path: string }) => {
    setSelectedOutput(output);
  };

  const handleDownload = async () => {
    if (!longPullItemId) return;
    await downloadZip({ longpullId: longPullItemId, fileName: `${projectData?.DisplayName}.zip` });
  };

  const handleGenerateClick = async () => {
    setLongPullItemId(-1);

    try {
      setIsFetchingData(true);

      await onBeforeGenerate?.();

      let createJobPayload: CreateJobPayload = {
        project_id: projectData.ID,
        document_templates: selectedTemplates,
        output_type: 'zip',
      };

      if (isQueriesEnabled) {
        if (!entityPrimaryKey) {
          console.error('Could not generate documents! No primary key');
          return;
        }

        const mappedAttributes = attributes?.map((item) => ({
          [entityPrimaryKey]: item[entityPrimaryKey],
          CLIENT_DISPLAY_NAME: item['CLIENT_DISPLAY_NAME'],
        }));

        createJobPayload = {
          ...createJobPayload,
          primary_key_ids: (mappedAttributes || []).map((item: any) => item[entityPrimaryKey]),
          attributes: mappedAttributes || [],
        };
      }

      if (selectedOutput?.type === 'folder') {
        createJobPayload = {
          ...createJobPayload,
          output_type: 'folder',
          output_folder_id: selectedOutput.folderId || projectData.FolderID,
        };
      }

      // generate job
      // @todo switch to rtk mutation
      const result = await getAxiosApiV2WithAuthorization({
        url: `${config.assetServiceBaseUrl}/document-build-jobs`,
        method: 'POST',
        data: createJobPayload,
      });

      // dispatch job queue - for now it will be here later it will be
      // an endpoint to dynamically get pending queries
      if (result?.data?.ID) {
        const newQueueItem = {
          id: result.data.ID,
          isUploading: true,
          isCompleted: false,
          completedFiles: 0,
          totalFiles: 0,
          message: `Starting to generate documents`,
          type: 'running',
          isInlineProgress: true,
        };

        setLongPullItemId(result.data.ID);
        setGeneratedForOutputType(selectedOutput?.type ?? 'zip');
        dispatch(addLongPullUIItem({ item: newQueueItem }));
      } else {
        console.error('Could not start long pull: no job ID provided!');
      }

      // reset hook form to keep state for generated job
      reset(getValues());

      setIsFetchingData(false);
    } catch (error: any) {
      setIsFetchingData(false);
      dispatch(
        addNotification({
          type: 'error',
          message: "Couldn't generate documents. Please try again.",
        }),
      );
      console.error(error);
    }
  };

  if (projectTemplatesToPickList?.length <= 0) {
    return null;
  }

  return (
    <NgCard>
      <NgCardContent>
        <>
          <StyledSectionHeader>
            <NgCardTitle title="Documents" />

            <StyledPreviewButton
              type="button"
              disabled={isFetchingData || isGenerating || !selectedTemplates?.length}
              onClick={() => setIsPreviewOpen(true)}
            >
              Preview
            </StyledPreviewButton>
          </StyledSectionHeader>
        </>

        <GenerateDocumentsSelector
          isQueryValid={isQueryValid}
          isQueriesEnabled={isQueriesEnabled}
          options={projectTemplatesToPickList}
          queriesResultsMap={queriesResultsMap}
          onChange={({ selectedItems, totalCount }) => {
            setValue('selectedDocumentTemplates', selectedItems, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            setTotalCount(totalCount);
            if (longPullItemId) {
              setLongPullItemId(null);
            }
          }}
        />

        <NgCardTitle title="Select output: " />

        <OutputSelector
          rootFolderId={projectData?.DocumentsFolderID}
          projectName={projectData?.DisplayName}
          onChange={handleOutputSelectionChange}
          initialOutput={selectedOutput}
        />

        <br />

        <DocumentsGenerateButton
          onClick={isDownloadReady ? handleDownload : handleGenerateClick}
          disabled={!isDownloadReady && (isFetchingData || generateButtonState?.disabled)}
          errorMessage={isDownloadReady ? null : generateButtonState?.message}
          isLoading={isFetchingData || isGenerating}
          loadingMessage={generatingMessage}
          isDownloadReady={isDownloadReady}
          label={
            <>
              Generate {totalCount > 0 ? totalCount : ''}
              {totalCount === 1 ? ' document' : totalCount > 1 ? ' documents' : ''}
            </>
          }
        />
      </NgCardContent>

      <DocumentsPreviewDialog
        open={isPreviewOpen}
        title={projectData?.DisplayName}
        templates={previewTemplates}
        onClose={() => setIsPreviewOpen(false)}
      />
    </NgCard>
  );
};
